import React from "react";

import type {
  SearchResult,
  SearchScope,
  SearchSuggestionGroups,
} from "types/search";
import type { WikiSuggestion } from "util/constants";
import type { SearchLocale } from "./searchCopy";
import {
  getSearchCopy,
} from "./searchCopy";
import SearchIssueSuggestionItem from "./SearchIssueSuggestionItem";
import SearchResultItem from "./SearchResultItem";

interface Props {
  groups: SearchSuggestionGroups;
  wikipediaSuggestions: WikiSuggestion[];
  locale: SearchLocale;
  onSeeAllSection: (scope: SearchScope) => void;
  onSelectIssueSuggestion: (suggestion: WikiSuggestion) => void;
  onSelectResult: (result: SearchResult) => void;
}

const SEARCH_SECTIONS: Array<{
  id: keyof SearchSuggestionGroups;
  labelKey: "issues" | "posts" | "people" | "tags";
  scope: SearchScope;
}> = [
  { id: "issues", labelKey: "issues", scope: "issues" },
  { id: "posts", labelKey: "posts", scope: "posts" },
  { id: "actors", labelKey: "people", scope: "actors" },
  { id: "tags", labelKey: "tags", scope: "tags" },
];

const SearchSuggestionsPanel: React.FC<Props> = ({
  groups,
  wikipediaSuggestions,
  locale,
  onSeeAllSection,
  onSelectIssueSuggestion,
  onSelectResult,
}) => {
  const hasResults = SEARCH_SECTIONS.some((section) =>
    section.id === "issues"
      ? groups.issues.length > 0 || wikipediaSuggestions.length > 0
      : groups[section.id].length > 0
  );

  if (!hasResults) {
    return null;
  }

  return (
    <>
      {SEARCH_SECTIONS.map((section) =>
        (
          section.id === "issues"
            ? groups.issues.length > 0 || wikipediaSuggestions.length > 0
            : groups[section.id].length > 0
        ) ? (
          <React.Fragment key={section.id}>
            <li className="px-2 pt-2 pb-1 text-left">
              <div className="flex items-center justify-between gap-2">
                <span className="text-xs text-eoe-text-meta dark:text-eoe-dark-text-meta">
                  {getSearchCopy(locale, section.labelKey)}
                </span>
                <button
                  type="button"
                  className="text-xs text-eoe-text-meta dark:text-eoe-dark-text-meta hover:text-eoe-text dark:hover:text-eoe-dark-text"
                  onClick={() => onSeeAllSection(section.scope)}
                >
                  {getSearchCopy(locale, "seeAll")}
                </button>
              </div>
            </li>
            {section.id === "issues"
              ? (
                  <>
                    {groups.issues.map((result) => (
                      <SearchResultItem
                        key={result.id}
                        result={result}
                        locale={locale}
                        compact
                        onSelect={onSelectResult}
                      />
                    ))}
                    {wikipediaSuggestions.map((suggestion, index) => (
                      <SearchIssueSuggestionItem
                        key={`${suggestion.cname}-${index}`}
                        suggestion={suggestion}
                        onSelect={onSelectIssueSuggestion}
                      />
                    ))}
                  </>
                )
              : groups[section.id].map((result) => (
                  <SearchResultItem
                    key={result.id}
                    result={result}
                    locale={locale}
                    compact
                    onSelect={onSelectResult}
                  />
                ))}
          </React.Fragment>
        ) : null
      )}
    </>
  );
};

export default SearchSuggestionsPanel;
